/**
 * Timeline Analyzer for Visa Interview Preparation
 *
 * Builds a unified employment and education timeline,
 * detects gaps and overlaps, and calculates experience metrics.
 */

import type {
  TimelineEntry,
  TimelineAnalysis,
  Position,
  Education,
} from '../types.js';

/**
 * US location patterns for detecting US-based experience
 */
const US_LOCATION_PATTERNS = [
  // States
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado',
  'connecticut', 'delaware', 'florida', 'georgia', 'hawaii', 'idaho',
  'illinois', 'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana',
  'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota', 'mississippi',
  'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey',
  'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma',
  'oregon', 'pennsylvania', 'rhode island', 'south carolina', 'south dakota',
  'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington',
  'west virginia', 'wisconsin', 'wyoming',
  // Common abbreviations and patterns
  'united states', 'usa', 'u.s.a', 'u.s.', ' us,', ' us$',
  // Major cities
  'new york', 'los angeles', 'chicago', 'houston', 'phoenix', 'philadelphia',
  'san antonio', 'san diego', 'dallas', 'san jose', 'austin', 'jacksonville',
  'fort worth', 'columbus', 'charlotte', 'san francisco', 'indianapolis',
  'seattle', 'denver', 'washington dc', 'boston', 'detroit', 'portland',
  'las vegas', 'memphis', 'baltimore', 'atlanta', 'miami', 'minneapolis',
  // Tech hubs
  'silicon valley', 'bay area', 'san francisco bay', 'palo alto', 'mountain view',
  'menlo park', 'sunnyvale', 'santa clara', 'cupertino', 'redwood city',
  'fremont', 'oakland', 'san mateo', 'redmond', 'bellevue', 'kirkland',
  'cambridge', 'somerville', 'arlington', 'reston', 'tysons',
  // State abbreviations (be careful with these - use with context)
  ', ca', ', ny', ', tx', ', wa', ', ma', ', il', ', pa', ', fl',
  ', az', ', ga', ', nc', ', nj', ', va', ', co', ', oh', ', mi',
  ', or', ', mn', ', wi', ', md', ', mo', ', in', ', tn', ', ct',
];

/**
 * Home country patterns (will vary by user, this is a generic set)
 * These are common non-US countries for visa applicants
 */
const NON_US_PATTERNS = [
  'india', 'china', 'philippines', 'vietnam', 'brazil', 'mexico',
  'canada', 'uk', 'united kingdom', 'germany', 'france', 'italy',
  'spain', 'japan', 'korea', 'south korea', 'taiwan', 'singapore',
  'australia', 'nigeria', 'pakistan', 'bangladesh', 'iran', 'russia',
  'mumbai', 'delhi', 'bangalore', 'bengaluru', 'hyderabad', 'chennai',
  'kolkata', 'pune', 'ahmedabad', 'jaipur', 'surat', 'lucknow',
  'beijing', 'shanghai', 'shenzhen', 'guangzhou', 'hangzhou', 'chengdu',
  'london', 'paris', 'berlin', 'tokyo', 'seoul', 'toronto', 'vancouver',
  'sydney', 'melbourne', 'hong kong', 'dublin', 'amsterdam',
];

/**
 * Build a unified timeline from positions and education
 */
export function buildTimeline(positions: Position[], education: Education[]): TimelineAnalysis {
  const entries: TimelineEntry[] = [];

  // Convert positions to timeline entries
  for (const pos of positions) {
    if (!pos.startedOn) continue;

    const endDate = pos.finishedOn || new Date();
    const durationDays = Math.floor(
      (endDate.getTime() - pos.startedOn.getTime()) / (1000 * 60 * 60 * 24)
    );

    entries.push({
      type: 'position',
      label: pos.title || 'Unknown Role',
      organization: pos.companyName || 'Unknown Company',
      location: pos.location || '',
      startDate: pos.startedOn,
      endDate: pos.finishedOn,
      durationDays,
      details: pos,
    });
  }

  // Convert education to timeline entries
  for (const edu of education) {
    if (!edu.startDate) continue;

    const endDate = edu.endDate || new Date();
    const durationDays = Math.floor(
      (endDate.getTime() - edu.startDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    entries.push({
      type: 'education',
      label: edu.degreeName || 'Education',
      organization: edu.schoolName || 'Unknown School',
      location: '', // Education entries typically don't have location in LinkedIn export
      startDate: edu.startDate,
      endDate: edu.endDate,
      durationDays,
      details: edu,
    });
  }

  // Sort by start date (newest first)
  entries.sort((a, b) => {
    const dateA = a.startDate?.getTime() || 0;
    const dateB = b.startDate?.getTime() || 0;
    return dateB - dateA;
  });

  // Detect gaps (considering education as legitimate gap-fillers)
  const positionEntries2 = entries.filter(e => e.type === 'position');
  const educationEntries = entries.filter(e => e.type === 'education');
  const gaps = detectGaps(positionEntries2, educationEntries);

  // Detect overlaps
  const overlaps = detectOverlaps(entries);

  // Calculate metrics
  const positionEntries = entries.filter(e => e.type === 'position');

  let totalUSExperienceDays = 0;
  let totalHomeCountryExperienceDays = 0;

  for (const entry of positionEntries) {
    if (isUSLocation(entry.location)) {
      totalUSExperienceDays += entry.durationDays;
    } else if (entry.location && isNonUSLocation(entry.location)) {
      totalHomeCountryExperienceDays += entry.durationDays;
    }
  }

  // Calculate total career span
  const sortedByStart = [...positionEntries]
    .filter(e => e.startDate)
    .sort((a, b) => (a.startDate?.getTime() || 0) - (b.startDate?.getTime() || 0));

  let totalCareerSpanDays = 0;
  if (sortedByStart.length > 0) {
    const firstStart = sortedByStart[0].startDate;
    const lastEnd = sortedByStart[sortedByStart.length - 1].endDate || new Date();
    if (firstStart) {
      totalCareerSpanDays = Math.floor(
        (lastEnd.getTime() - firstStart.getTime()) / (1000 * 60 * 60 * 24)
      );
    }
  }

  // Calculate average tenure
  const totalPositionDays = positionEntries.reduce((sum, e) => sum + e.durationDays, 0);
  const averageTenureDays = positionEntries.length > 0
    ? Math.floor(totalPositionDays / positionEntries.length)
    : 0;

  // Count short tenures (< 12 months)
  const shortTenureCount = positionEntries.filter(e => e.durationDays < 365).length;

  return {
    entries,
    gaps,
    overlaps,
    totalUSExperienceDays,
    totalHomeCountryExperienceDays,
    totalCareerSpanDays,
    averageTenureDays,
    positionCount: positionEntries.length,
    shortTenureCount,
  };
}

/**
 * Subtract education periods from a gap, returning remaining uncovered periods
 */
function subtractEducationFromGap(
  gapStart: Date,
  gapEnd: Date,
  education: TimelineEntry[]
): Array<{ start: Date; end: Date; days: number }> {
  const gapStartMs = gapStart.getTime();
  const gapEndMs = gapEnd.getTime();

  // Collect all education intervals that overlap with the gap
  const eduIntervals: Array<{ start: number; end: number }> = [];
  for (const edu of education) {
    if (!edu.startDate) continue;
    const eduStart = edu.startDate.getTime();
    const eduEnd = (edu.endDate || new Date()).getTime();

    // Check if education overlaps with gap
    if (eduStart < gapEndMs && eduEnd > gapStartMs) {
      eduIntervals.push({
        start: Math.max(eduStart, gapStartMs),
        end: Math.min(eduEnd, gapEndMs),
      });
    }
  }

  if (eduIntervals.length === 0) {
    // No education covers this gap
    const days = Math.floor((gapEndMs - gapStartMs) / (1000 * 60 * 60 * 24));
    return [{ start: gapStart, end: gapEnd, days }];
  }

  // Sort and merge overlapping education intervals
  eduIntervals.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  let current = { ...eduIntervals[0] };

  for (let i = 1; i < eduIntervals.length; i++) {
    if (eduIntervals[i].start <= current.end) {
      current.end = Math.max(current.end, eduIntervals[i].end);
    } else {
      merged.push(current);
      current = { ...eduIntervals[i] };
    }
  }
  merged.push(current);

  // Find uncovered portions of the gap
  const uncovered: Array<{ start: Date; end: Date; days: number }> = [];
  let pointer = gapStartMs;

  for (const edu of merged) {
    if (pointer < edu.start) {
      // There's an uncovered portion before this education period
      const days = Math.floor((edu.start - pointer) / (1000 * 60 * 60 * 24));
      if (days > 0) {
        uncovered.push({
          start: new Date(pointer),
          end: new Date(edu.start),
          days,
        });
      }
    }
    pointer = Math.max(pointer, edu.end);
  }

  // Check for uncovered portion after all education
  if (pointer < gapEndMs) {
    const days = Math.floor((gapEndMs - pointer) / (1000 * 60 * 60 * 24));
    if (days > 0) {
      uncovered.push({
        start: new Date(pointer),
        end: gapEnd,
        days,
      });
    }
  }

  return uncovered;
}

/**
 * Detect true employment gaps by finding periods where no position was active
 * Uses interval merging and properly subtracts education periods
 */
function detectGaps(positions: TimelineEntry[], education: TimelineEntry[]): TimelineEntry[] {
  const gaps: TimelineEntry[] = [];

  // Filter to positions with valid dates
  const validPositions = positions.filter(p => p.startDate);
  if (validPositions.length === 0) return gaps;

  // Convert positions to intervals [start, end] in milliseconds
  const intervals: Array<{ start: number; end: number; entry: TimelineEntry }> = validPositions.map(p => ({
    start: p.startDate!.getTime(),
    end: (p.endDate || new Date()).getTime(),
    entry: p,
  }));

  // Sort by start time
  intervals.sort((a, b) => a.start - b.start);

  // Merge overlapping intervals to find continuous employment periods
  const mergedIntervals: Array<{ start: number; end: number }> = [];
  let current = { start: intervals[0].start, end: intervals[0].end };

  for (let i = 1; i < intervals.length; i++) {
    const interval = intervals[i];
    // Allow 14-day overlap for job transitions
    if (interval.start <= current.end + (14 * 24 * 60 * 60 * 1000)) {
      // Overlapping or adjacent - extend current interval
      current.end = Math.max(current.end, interval.end);
    } else {
      // Gap found - save current and start new
      mergedIntervals.push(current);
      current = { start: interval.start, end: interval.end };
    }
  }
  mergedIntervals.push(current);

  // Find gaps between merged intervals
  for (let i = 0; i < mergedIntervals.length - 1; i++) {
    const gapStart = new Date(mergedIntervals[i].end);
    const gapEnd = new Date(mergedIntervals[i + 1].start);

    // Subtract education periods from this gap
    const uncoveredPeriods = subtractEducationFromGap(gapStart, gapEnd, education);

    // Find what position ended just before this gap
    const precedingPosition = validPositions.find(p =>
      p.endDate && Math.abs(p.endDate.getTime() - gapStart.getTime()) < (15 * 24 * 60 * 60 * 1000)
    );

    // Only flag uncovered periods > 45 days
    for (const period of uncoveredPeriods) {
      if (period.days > 45) {
        gaps.push({
          type: 'gap',
          label: precedingPosition ? `Gap after ${precedingPosition.organization}` : 'Employment gap',
          organization: '',
          location: '',
          startDate: period.start,
          endDate: period.end,
          durationDays: period.days,
          gapType: 'employment',
        });
      }
    }
  }

  // Sort gaps by duration (longest first)
  gaps.sort((a, b) => b.durationDays - a.durationDays);

  return gaps;
}

/**
 * Detect overlapping employment
 */
function detectOverlaps(entries: TimelineEntry[]): TimelineAnalysis['overlaps'] {
  const overlaps: TimelineAnalysis['overlaps'] = [];

  // Only check positions (not education) for concerning overlaps
  const positions = entries.filter(e => e.type === 'position' && e.startDate);

  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const entry1 = positions[i];
      const entry2 = positions[j];

      const start1 = entry1.startDate;
      const end1 = entry1.endDate || new Date();
      const start2 = entry2.startDate;
      const end2 = entry2.endDate || new Date();

      if (!start1 || !start2) continue;

      // Check for overlap
      const overlapStart = new Date(Math.max(start1.getTime(), start2.getTime()));
      const overlapEnd = new Date(Math.min(end1.getTime(), end2.getTime()));

      if (overlapStart < overlapEnd) {
        const overlapDays = Math.floor(
          (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (overlapDays > 7) { // More than a week overlap
          overlaps.push({
            entry1,
            entry2,
            overlapDays,
          });
        }
      }
    }
  }

  // Sort by overlap duration (longest first)
  overlaps.sort((a, b) => b.overlapDays - a.overlapDays);

  return overlaps;
}

/**
 * Check if a location is in the US
 */
function isUSLocation(location: string): boolean {
  if (!location) return false;
  const lower = location.toLowerCase();
  return US_LOCATION_PATTERNS.some(pattern => lower.includes(pattern));
}

/**
 * Check if a location is outside the US (common home countries)
 */
function isNonUSLocation(location: string): boolean {
  if (!location) return false;
  const lower = location.toLowerCase();
  return NON_US_PATTERNS.some(pattern => lower.includes(pattern));
}

/**
 * Format timeline for display
 */
export function formatTimeline(analysis: TimelineAnalysis): string {
  const lines: string[] = [];

  lines.push('=== EMPLOYMENT & EDUCATION TIMELINE ===\n');

  // Sort entries by start date (oldest first for chronological view)
  const chronological = [...analysis.entries]
    .filter(e => e.startDate)
    .sort((a, b) => (a.startDate?.getTime() || 0) - (b.startDate?.getTime() || 0));

  for (const entry of chronological) {
    const typeIcon = entry.type === 'position' ? '[WORK]' : '[EDU]';
    const dateRange = formatDateRange(entry.startDate, entry.endDate);
    const duration = formatDuration(entry.durationDays);
    const location = entry.location ? ` (${entry.location})` : '';

    lines.push(`${typeIcon} ${entry.organization}${location}`);
    lines.push(`       ${entry.label}`);
    lines.push(`       ${dateRange} | ${duration}`);
    lines.push('');
  }

  // Summary stats
  lines.push('=== SUMMARY ===');
  lines.push(`Total positions: ${analysis.positionCount}`);
  lines.push(`Total career span: ${formatDuration(analysis.totalCareerSpanDays)}`);
  lines.push(`Average tenure: ${formatDuration(analysis.averageTenureDays)}`);
  lines.push(`US experience: ${formatDuration(analysis.totalUSExperienceDays)}`);
  lines.push(`Non-US experience: ${formatDuration(analysis.totalHomeCountryExperienceDays)}`);
  lines.push(`Short tenures (<12 months): ${analysis.shortTenureCount}`);
  lines.push(`Employment gaps detected: ${analysis.gaps.length}`);
  lines.push(`Overlapping positions: ${analysis.overlaps.length}`);

  return lines.join('\n');
}

/**
 * Format a date range for display
 */
function formatDateRange(start: Date | null, end: Date | null): string {
  const formatDate = (d: Date | null) => {
    if (!d) return 'Present';
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
}

/**
 * Format duration in days to human-readable string
 */
function formatDuration(days: number): string {
  if (days < 30) {
    return `${days} days`;
  }

  const months = Math.round(days / 30);
  if (months < 12) {
    return `${months} month${months !== 1 ? 's' : ''}`;
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }

  return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
}

/**
 * Calculate years of US experience
 */
export function getUSExperienceYears(analysis: TimelineAnalysis): number {
  return Math.round((analysis.totalUSExperienceDays / 365) * 10) / 10;
}

/**
 * Calculate years of non-US experience
 */
export function getNonUSExperienceYears(analysis: TimelineAnalysis): number {
  return Math.round((analysis.totalHomeCountryExperienceDays / 365) * 10) / 10;
}

/**
 * Get the longest employment gap
 */
export function getLongestGap(analysis: TimelineAnalysis): TimelineEntry | null {
  if (analysis.gaps.length === 0) return null;
  return analysis.gaps.reduce((longest, gap) =>
    gap.durationDays > longest.durationDays ? gap : longest
  );
}

/**
 * Identify potential interview questions based on timeline
 */
export function generateTimelineQuestions(analysis: TimelineAnalysis): string[] {
  const questions: string[] = [];

  // Gap-related questions
  for (const gap of analysis.gaps) {
    if (gap.durationDays >= 90) {
      const months = Math.round(gap.durationDays / 30);
      questions.push(
        `"I see a ${months}-month gap in your employment after ${gap.label.replace('Gap after ', '')}. ` +
        `What were you doing during this time?"`
      );
    }
  }

  // Short tenure questions
  if (analysis.shortTenureCount >= 2) {
    questions.push(
      `"You've had ${analysis.shortTenureCount} positions lasting less than a year. ` +
      `Can you explain why you left these roles?"`
    );
  }

  // Overlap questions
  for (const overlap of analysis.overlaps) {
    if (overlap.overlapDays >= 30) {
      const months = Math.round(overlap.overlapDays / 30);
      questions.push(
        `"It looks like you worked at ${overlap.entry1.organization} and ${overlap.entry2.organization} ` +
        `simultaneously for about ${months} month${months !== 1 ? 's' : ''}. Can you explain this overlap?"`
      );
    }
  }

  // Career progression questions
  if (analysis.positionCount >= 5) {
    questions.push(
      `"You've had ${analysis.positionCount} positions over your career. ` +
      `Can you walk me through your career progression and what motivated each move?"`
    );
  }

  // US experience questions
  const usYears = getUSExperienceYears(analysis);
  if (usYears >= 5) {
    questions.push(
      `"You've been working in the US for about ${Math.round(usYears)} years. ` +
      `What are your ties to your home country?"`
    );
  }

  return questions;
}
